import { queryOptions } from '@tanstack/react-query';

import type { PendingPhotoDto } from './generated';
import {
  usersControllerApprovePending,
  usersControllerCancelOwnPending,
  usersControllerGetPendingByUser,
  usersControllerListPending,
  usersControllerRejectPending,
} from './generated';

export type PendingPhoto = PendingPhotoDto;

/**
 * One row of the moderation queue (`/users/pending`). Same payload as the self
 * view: the server projects both through `pendingPhotoSchema`, so the row the
 * queue lists and the row a user sees of their own submission are one type.
 * Named apart because the review dialog and the tab read it as a queue row.
 */
export type PendingQueueRow = PendingPhotoDto;

export async function listPending(): Promise<PendingQueueRow[]> {
  const { data } = await usersControllerListPending({ throwOnError: true });
  return data;
}

export const pendingQueryKey = (oid: string) =>
  ['users', oid, 'pending'] as const;

/** Count of everything awaiting moderation — the badge in the top bar. */
export const pendingCountQueryKey = ['photos', 'pending', 'count'] as const;

/**
 * `/users/:oid/pending` answers for the reader themselves as well as for a
 * moderator (the server delegates both to the same projection), and `null`
 * is a normal state: nothing awaiting moderation.
 */
export const pendingQueryOptions = (oid: string) =>
  queryOptions({
    queryKey: pendingQueryKey(oid),
    queryFn: async () => {
      const { data } = await usersControllerGetPendingByUser({
        path: { userId: oid },
        throwOnError: true,
      });
      return data.pending;
    },
    staleTime: 30_000,
  });

export async function approvePending(id: string): Promise<void> {
  await usersControllerApprovePending({ path: { id }, throwOnError: true });
}

export async function rejectPending(
  id: string,
  reason?: string,
): Promise<void> {
  await usersControllerRejectPending({
    path: { id },
    body: { reason },
    throwOnError: true,
  });
}

/**
 * Withdraw one's own submission. The endpoint is self-only by construction —
 * there is no oid to pass.
 */
export async function cancelOwnPending(): Promise<void> {
  await usersControllerCancelOwnPending({ throwOnError: true });
}
